import React, { useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';
import { NavLink, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
const AQ_PRICING_BASE_URL = process.env.REACT_APP_AQ_PRICING_BASE_URL;

// API URLs
const API_URL = `${BQ_BASE_URL}/bq/api/rooms/`;
const AVAILABILITY_API_URL = `${BQ_BASE_URL}/bq/api/availability`;
const FACTORS_API_URL = `${AQ_PRICING_BASE_URL}/pricing/api/get-factors`;

// ============================================
// CONDITIONS — same codes as RoomManagement
// ============================================
const CONDITIONS = [
  { code: 'all_working', label: 'All Working' },
  { code: 'ac_not_working', label: 'AC Not Working' },
  { code: 'geyser_not_working', label: 'Geyser Not Working' },
  { code: 'tv_not_working', label: 'TV Not Working' },
  { code: 'balcony_blocked', label: 'Balcony Blocked' },
  { code: 'noise_issue', label: 'Noise Issue' },
  { code: 'under_maintenance', label: 'Under Maintenance' },
];

const normalizeCondition = (raw) => {
  if (!raw) return 'all_working';
  const lower = String(raw).toLowerCase().trim().replace(/\s+/g, '_');
  const match = CONDITIONS.find((c) => c.code === lower);
  return match ? match.code : 'all_working';
};

const RoomAssignment = () => {
  const navigate = useNavigate();
  const [roomData, setRoomData] = useState({});
  const [availableRoomTypes, setAvailableRoomTypes] = useState([]);
  const [isPopOpen, setIsPopOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  // Factors (amenity master list)
  const [roomAmenities, setRoomAmenities] = useState([]);
  const [allFactors, setAllFactors] = useState([]);
  const [factorsLoading, setFactorsLoading] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roomStatus, setRoomStatus] = useState('all');
  const [selectedRoomType, setSelectedRoomType] = useState('all');
  const [onlyAvailableRooms, setOnlyAvailableRooms] = useState(false);
  const [conditionFilter, setConditionFilter] = useState('all');

  // ============================================
  // Helpers
  // ============================================
  const getConditionLabel = (code) => {
    const c = CONDITIONS.find((x) => x.code === code);
    return c ? c.label : 'All Working';
  };

  const getConditionColor = (code) => {
    if (!code || code === 'all_working') return 'text-green-700';
    if (code === 'under_maintenance') return 'text-red-700';
    return 'text-orange-700';
  };

  const getStatusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'available') return 'bg-green-100 text-green-800';
    if (s === 'occupied') return 'bg-red-100 text-red-800';
    if (s === 'reserved') return 'bg-yellow-100 text-yellow-800';
    if (s === 'under maintenance') return 'bg-gray-200 text-gray-800';
    return 'bg-gray-100 text-gray-800';
  };

  // ============================================
  // Fetch amenity factors
  // ============================================
  const fetchRoomAmenityFactors = async () => {
    setFactorsLoading(true);
    try {
      const response = await fetch(FACTORS_API_URL, {
        method: 'GET',
        headers: { accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to fetch factors');
      const data = await response.json();

      setAllFactors(data || []);

      const amenities = (data || [])
        .filter((f) => f.is_room_amenity === true && f.isactive === true)
        .map((f) => ({
          factorid: f.factorid,
          code: String(f.factorid),
          label: f.factorname,
          value: f.default_value ?? f.defaultpercentage ?? 0,
          value_type: f.value_type || 'flat',
        }));

      setRoomAmenities(amenities);
    } catch (error) {
      console.error('Error fetching factors:', error);
      toast.error('Could not load room amenities');
      setRoomAmenities([]);
    } finally {
      setFactorsLoading(false);
    }
  };

  // ============================================
  // Fetch rooms
  // ============================================
  const fetchRoomDetails = async () => {
    try {
      const response = await fetch(API_URL, { headers: { accept: '*/*' } });
      const data = await response.json();

      const list = Array.isArray(data) ? data : data?.rooms || [];

      const normalized = list.map((r) => {
        const amenityObjects = (r.amenities || []).map((a) => {
          if (typeof a === 'object' && a !== null) {
            return {
              factorid: a.factorid,
              name: a.name,
              value: a.value,
              value_type: a.value_type,
              contribution: a.contribution,
            };
          }
          const found = allFactors.find((f) => String(f.factorid) === String(a));
          return {
            factorid: Number(a),
            name: found ? found.factorname : String(a),
            value: found ? (found.default_value ?? found.defaultpercentage ?? 0) : 0,
            value_type: found ? found.value_type || 'flat' : 'flat',
            contribution: 0,
          };
        });

        const amenityIds = amenityObjects.map((a) => String(a.factorid));

        return {
          roomid: r.roomid,
          roomnumber: r.roomnumber,
          roomtypeid: r.roomtypeid,
          status: r.status,
          auto_assign_room: r.auto_assign_room || false,
          amenities: amenityObjects,
          amenityIds,
          condition: normalizeCondition(r.condition),
          condition_notes: r.condition_notes || '',
          roomtype: r.roomtype?.roomtypename || '',
          room_type: r.roomtype?.roomtypename || '',
          baseprice: r.roomtype?.baseprice ?? 0,
          base_price: r.base_price ?? r.roomtype?.baseprice ?? 0,
          amenities_total: r.amenities_total ?? 0,
          flat_total: r.flat_total ?? 0,
          percentage_total: r.percentage_total ?? 0,
          final_price: r.final_price ?? r.roomtype?.baseprice ?? 0,
        };
      });

      // Group: roomType → status (lowercase) → [rooms]
      const grouped = {};
      normalized.forEach((room) => {
        const rtype = room.roomtype || 'Unknown';
        const status = String(room.status || '').toLowerCase();

        if (!grouped[rtype]) grouped[rtype] = {};
        if (!grouped[rtype][status]) grouped[rtype][status] = [];
        grouped[rtype][status].push(room);
      });

      setRoomData(grouped);
    } catch (error) {
      console.error('Error fetching room details:', error);
      toast.error('Could not load rooms');
    }
  };

  const fetchAvailableRoomTypes = async () => {
    try {
      const response = await fetch(AVAILABILITY_API_URL);
      const data = await response.json();
      setAvailableRoomTypes(data.available_room_types);
    } catch (error) {
      console.error('Error fetching available room types:', error);
      setAvailableRoomTypes([]);
    }
  };

  useEffect(() => {
    // Fetch factors first so amenities can be resolved on room fetch
    (async () => {
      await fetchRoomAmenityFactors();
      await fetchRoomDetails();
    })();
    fetchAvailableRoomTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // Filter rooms
  // ============================================
  const filteredRooms = Object.keys(roomData).map((roomType) => {
    const roomsInType = Object.keys(roomData[roomType]).flatMap((status) =>
      roomData[roomType][status].filter((room) => {
        const matchesSearch =
          (room?.baseprice?.toString()?.includes(searchTerm) ?? false) ||
          (room?.roomnumber?.toString()?.includes(searchTerm) ?? false);

        const matchesStatus =
          roomStatus === 'all' || String(room.status).toLowerCase() === roomStatus;

        const matchesRoomType =
          selectedRoomType === 'all' ||
          String(room.room_type).toLowerCase() === selectedRoomType.toLowerCase();

        const matchesAvailability =
          !onlyAvailableRooms || String(room.status).toLowerCase() === 'available';

        const matchesCondition = conditionFilter === 'all' || room.condition === conditionFilter;

        return (
          matchesSearch &&
          matchesStatus &&
          matchesRoomType &&
          matchesAvailability &&
          matchesCondition
        );
      })
    );

    const availableCount = roomsInType.filter(
      (r) => String(r.status).toLowerCase() === 'available'
    ).length;
    const occupiedCount = roomsInType.filter(
      (r) => String(r.status).toLowerCase() === 'occupied'
    ).length;
    const reservedCount = roomsInType.filter(
      (r) => String(r.status).toLowerCase() === 'reserved'
    ).length;
    const maintenanceCount = roomsInType.filter(
      (r) => String(r.status).toLowerCase() === 'under maintenance'
    ).length;

    return {
      roomType,
      availableCount,
      occupiedCount,
      reservedCount,
      maintenanceCount,
      rooms: roomsInType,
    };
  });

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoomStatus('all');
    setSelectedRoomType('all');
    setOnlyAvailableRooms(false);
    setConditionFilter('all');
  };

  const openModal = (room) => {
    setSelectedRoom(room);
    setIsPopOpen(true);
  };

  const modalClose = () => {
    setIsPopOpen(false);
    setSelectedRoom(null);
  };

  const handleUpdate = async () => {
    toast.error(
      <div>
        <p className="font-bold">Room status cannot be changed here!</p>
        <p className="text-sm mt-1">
          Please go to{' '}
          <button
            onClick={() => {
              toast.dismiss();
              navigate(
                '/dashboard/core-modules/property-management-system/master-data/room-management'
              );
            }}
            className="text-blue-600 underline font-semibold hover:text-blue-800"
          >
            Master Data → Room Management
          </button>{' '}
          to change room status.
        </p>
      </div>,
      {
        position: 'top-center',
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: false,
        draggable: false,
        className: 'bg-red-50 border-l-4 border-red-500',
      }
    );

    setTimeout(() => {
      modalClose();
    }, 1000);
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <NavLink
        to="/dashboard/core-modules/property-management-system/front-desk-operations"
        className="flex items-center text-gray-400 hover:text-black mb-4"
      >
        <FaArrowLeft className="mr-3 text-md" />
        <span className="text-lg">Back to Front Desk Operations</span>
      </NavLink>

      <h1 className="text-2xl font-bold text-center text-black">Room Assignment</h1>
      <h1 className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
        View all rooms, their amenities, pricing and condition before assigning guests.
      </h1>
      <hr className="border border-gray-400 w-1/2 mx-auto mb-7" />

      {/* ============================================ */}
      {/* Filters Section */}
      {/* ============================================ */}
      <div className="flex justify-between items-center gap-2 mb-8 text-sm w-full flex-wrap">
        <div className="w-full mb-4 sm:mb-0 relative flex-1 min-w-[180px]">
          <input
            type="text"
            className="w-full px-2 py-2 pl-10 border border-gray-300 rounded-md"
            placeholder="Search by Room Number"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FiSearch
            size={16}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600"
          />
        </div>

        <div className="w-full mb-4 sm:mb-0 flex-1 min-w-[150px]">
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            value={roomStatus}
            onChange={(e) => setRoomStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
            <option value="occupied">Occupied</option>
            <option value="under maintenance">Under Maintenance</option>
          </select>
        </div>

        <div className="w-full mb-4 sm:mb-0 flex-1 min-w-[150px]">
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none"
            value={selectedRoomType}
            onChange={(e) => setSelectedRoomType(e.target.value)}
          >
            <option value="all">All Room Types</option>
            {Object.keys(roomData).map((rt) => (
              <option key={rt} value={rt}>
                {rt}
              </option>
            ))}
          </select>
        </div>

        <div className="w-full mb-4 sm:mb-0 flex-1 min-w-[150px]">
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none"
            value={conditionFilter}
            onChange={(e) => setConditionFilter(e.target.value)}
          >
            <option value="all">All Conditions</option>
            {CONDITIONS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="w-full mr-4 flex-1 min-w-[120px]">
          <button
            onClick={handleClearFilters}
            className="w-full px-10 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* ============================================ */}
      {/* Status Summary Cards */}
      {/* ============================================ */}
      <div className="w-full mb-6">
        {filteredRooms.length > 0 ? (
          filteredRooms
            .filter(({ roomType }) => selectedRoomType === roomType)
            .map(
              ({
                roomType,
                availableCount,
                occupiedCount,
                reservedCount,
                maintenanceCount,
                rooms,
              }) => (
                <div key={roomType} className="mb-6">
                  <h2 className="text-lg font-bold mb-2 text-gray-800">{roomType}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Total Rooms</h3>
                      <p className="text-3xl font-bold text-gray-800">{rooms.length}</p>
                    </div>
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Available</h3>
                      <p className="text-3xl font-bold text-gray-800">{availableCount}</p>
                    </div>
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Reserved</h3>
                      <p className="text-3xl font-bold text-gray-800">{reservedCount}</p>
                    </div>
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Occupied</h3>
                      <p className="text-3xl font-bold text-gray-800">{occupiedCount}</p>
                    </div>
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Maintenance</h3>
                      <p className="text-3xl font-bold text-gray-800">{maintenanceCount}</p>
                    </div>
                  </div>
                </div>
              )
            )
        ) : (
          <div className="text-center text-gray-700 mt-4 p-4">
            <p className="text-lg font-semibold text-gray-800">Loading...</p>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* Room Tables with Amenities / Condition / Auto-Assign */}
      {/* ============================================ */}
      <div className="space-y-8 text-sm">
        {filteredRooms.map(({ roomType, rooms }) => {
          if (rooms.length === 0) return null;

          return (
            <div key={roomType}>
              <h2 className="bg-white text-black font-bold text-left p-3 text-base rounded-t-lg shadow-md">
                {roomType}
              </h2>

              <div className="overflow-x-auto rounded-b-lg">
                <table className="min-w-full table-auto bg-white">
                  <thead className="bg-gray-900 text-white">
                    <tr>
                      <th className="py-3 px-4 text-left font-medium">Room No.</th>
                      <th className="py-3 px-4 text-left font-medium">Status</th>
                      <th className="py-3 px-4 text-left font-medium">Base Price</th>
                      <th className="py-3 px-4 text-left font-medium">Amenities</th>
                      <th className="py-3 px-4 text-left font-medium">Amenities Total</th>
                      <th className="py-3 px-4 text-left font-medium">Final Price</th>
                      <th className="py-3 px-4 text-left font-medium">Condition</th>
                      <th className="py-3 px-4 text-left font-medium">Auto-Assign</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 border border-gray-200">
                    {rooms.map((room, index) => {
                      const amenities = room.amenities || [];
                      const condition = room.condition || 'all_working';

                      return (
                        <tr
                          key={room.roomid || index}
                          className={`${
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-100'
                          } hover:bg-gray-200 cursor-pointer align-top`}
                          onClick={() => openModal(room)}
                        >
                          <td className="py-3 px-4 font-semibold">{room.roomnumber}</td>

                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(
                                room.status
                              )}`}
                            >
                              {room.status}
                            </span>
                          </td>

                          <td className="py-3 px-4">₹{room.base_price}</td>

                          <td className="py-3 px-4">
                            {amenities.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {amenities.map((a, i) => (
                                  <span
                                    key={`${a.factorid}-${i}`}
                                    className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs"
                                  >
                                    {a.name}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">None</span>
                            )}
                          </td>

                          <td className="py-3 px-4 font-medium">
                            {room.amenities_total > 0 ? (
                              <span className="text-green-700">+₹{room.amenities_total}</span>
                            ) : (
                              <span className="text-gray-400">₹0</span>
                            )}
                          </td>

                          <td className="py-3 px-4 font-bold text-black">₹{room.final_price}</td>

                          <td className="py-3 px-4">
                            <span className={`font-semibold ${getConditionColor(condition)}`}>
                              {getConditionLabel(condition)}
                            </span>
                            {condition !== 'all_working' && room.condition_notes && (
                              <p className="text-xs text-gray-500 mt-0.5">{room.condition_notes}</p>
                            )}
                          </td>

                          <td className="py-3 px-4">
                            <label className="relative inline-flex items-center cursor-default pointer-events-none">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={room.auto_assign_room}
                                readOnly
                                disabled
                              />
                              <div className="w-10 h-6 bg-gray-300 rounded-full peer peer-checked:bg-black transition-all"></div>
                              <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-full"></div>
                            </label>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {/* ============================================ */}
      {/* Room Details Modal (view-only) */}
      {/* ============================================ */}
      {isPopOpen && selectedRoom && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="relative bg-white p-8 rounded-lg shadow-lg w-full max-w-2xl text-sm text-black max-h-[90vh] overflow-y-auto">
            <button
              onClick={modalClose}
              className="absolute top-4 right-3 text-red-500 hover:text-red-600"
            >
              <IoClose size={28} />
            </button>

            <h3 className="text-xl font-bold text-center">Room Details</h3>
            <p className="text-sm text-red-500 mb-4 text-center font-semibold bg-red-50 p-2 rounded-lg">
              ⚠️ Room status cannot be changed here. Please use Master Data → Room Management.
            </p>

            <div className="space-y-4 text-xs">
              {/* Room Number */}
              <div>
                <label className="block text-black font-medium">Room Number</label>
                <input
                  type="text"
                  value={selectedRoom.roomnumber}
                  className="w-full px-4 py-2 border rounded-md bg-gray-100 cursor-not-allowed"
                  disabled
                />
              </div>

              {/* Room Type */}
              <div>
                <label className="block text-black font-medium">Room Type</label>
                <input
                  type="text"
                  value={selectedRoom.room_type}
                  className="w-full px-4 py-2 border rounded-md bg-gray-100 cursor-not-allowed"
                  disabled
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-black font-medium">Status</label>
                <input
                  type="text"
                  value={selectedRoom.status}
                  className="w-full px-4 py-2 border rounded-md bg-gray-100 cursor-not-allowed"
                  disabled
                />
                <p className="text-xs text-red-500 mt-1">
                  * Status changes are disabled in this view
                </p>
              </div>

              {/* Base Price */}
              <div>
                <label className="block text-black font-medium">Base Price</label>
                <input
                  type="number"
                  value={selectedRoom.base_price}
                  className="w-full px-4 py-2 border rounded-md bg-gray-100 cursor-not-allowed"
                  disabled
                />
              </div>

              {/* Amenities */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-black text-sm font-semibold mb-2">
                  Room Amenities
                </label>
                {factorsLoading ? (
                  <p className="text-xs text-gray-400 italic">Loading amenities...</p>
                ) : (selectedRoom.amenities || []).length === 0 ? (
                  <p className="text-xs text-gray-500 italic">
                    No amenities assigned to this room.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {selectedRoom.amenities.map((a, i) => (
                      <div
                        key={`${a.factorid}-${i}`}
                        className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border-2 bg-blue-50 border-blue-400 text-blue-800 font-semibold"
                      >
                        <span className="text-xs flex-1">{a.name}</span>
                        <span className="text-[10px] text-gray-600">
                          {a.value_type === 'percentage' ? `${a.value}%` : `₹${a.value}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pricing breakdown */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-black text-sm font-semibold mb-2">
                  Pricing Breakdown
                </label>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex justify-between bg-gray-50 px-3 py-2 rounded">
                    <span>Base Price:</span>
                    <span className="font-semibold">₹{selectedRoom.base_price}</span>
                  </div>
                  <div className="flex justify-between bg-gray-50 px-3 py-2 rounded">
                    <span>Amenities Total:</span>
                    <span className="font-semibold text-green-700">
                      +₹{selectedRoom.amenities_total}
                    </span>
                  </div>
                  <div className="flex justify-between bg-gray-50 px-3 py-2 rounded">
                    <span>Flat Total:</span>
                    <span className="font-semibold">₹{selectedRoom.flat_total}</span>
                  </div>
                  <div className="flex justify-between bg-gray-50 px-3 py-2 rounded">
                    <span>Percentage Total:</span>
                    <span className="font-semibold">{selectedRoom.percentage_total}%</span>
                  </div>
                  <div className="flex justify-between bg-black text-white px-3 py-2 rounded col-span-2">
                    <span>Final Price:</span>
                    <span className="font-bold">₹{selectedRoom.final_price}</span>
                  </div>
                </div>
              </div>

              {/* Condition */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-black text-sm font-semibold mb-2">
                  Current Condition
                </label>
                <div
                  className={`w-full px-4 py-2 border rounded-md bg-gray-100 font-semibold ${getConditionColor(
                    selectedRoom.condition
                  )}`}
                >
                  {getConditionLabel(selectedRoom.condition)}
                </div>
                {selectedRoom.condition !== 'all_working' && selectedRoom.condition_notes && (
                  <p className="text-xs text-gray-500 mt-2 italic">
                    Notes: {selectedRoom.condition_notes}
                  </p>
                )}
              </div>

              {/* Auto-Assign */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-black text-sm font-semibold">
                      Auto-Assign Room
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      When enabled, this room is prioritized for automatic guest assignment.
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      selectedRoom.auto_assign_room
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {selectedRoom.auto_assign_room ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={modalClose}
                className="bg-red-500 text-white hover:bg-red-600 font-semibold px-6 py-2 rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover
        className="mt-12"
      />
    </div>
  );
};

export default RoomAssignment;

// import React, { useEffect, useState } from 'react';
// import { FiSearch } from 'react-icons/fi';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import 'react-datepicker/dist/react-datepicker.css';
// import { NavLink, useNavigate } from 'react-router-dom';
// import { FaArrowLeft } from 'react-icons/fa';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// // API URLs
// const API_URL = `${BQ_BASE_URL}/bq/api/rooms/search`;
// const AVAILABILITY_API_URL = `${BQ_BASE_URL}/bq/api/availability`;

// const RoomAssignment = () => {
//   const navigate = useNavigate();
//   const [roomData, setRoomData] = useState({});
//   const [availableRoomTypes, setAvailableRoomTypes] = useState([]);
//   const [isPopOpen, setIsPopOpen] = useState(false);
//   const [selectedRoom, setSelectedRoom] = useState(null);
//   const [updatedRoom, setUpdatedRoom] = useState(selectedRoom);

//   // Filter states
//   const [searchTerm, setSearchTerm] = useState('');
//   const [roomStatus, setRoomStatus] = useState('all');
//   const [selectedRoomType, setSelectedRoomType] = useState('all');
//   const [onlyAvailableRooms, setOnlyAvailableRooms] = useState(false);

//   useEffect(() => {
//     setUpdatedRoom(selectedRoom);
//   }, [selectedRoom]);

//   const handleUpdate = async () => {
//     // Show error toast that room status cannot be changed here
//     toast.error(
//       <div>
//         <p className="font-bold">Room status cannot be changed here!</p>
//         <p className="text-sm mt-1">
//           Please go to{' '}
//           <button
//             onClick={() => {
//               toast.dismiss();
//               navigate(
//                 '/dashboard/core-modules/property-management-system/master-data/room-management'
//               );
//             }}
//             className="text-blue-600 underline font-semibold hover:text-blue-800"
//           >
//             Master Data → Room Management
//           </button>{' '}
//           to change room status.
//         </p>
//       </div>,
//       {
//         position: 'top-center',
//         autoClose: 8000,
//         hideProgressBar: false,
//         closeOnClick: false,
//         draggable: false,
//         className: 'bg-red-50 border-l-4 border-red-500',
//       }
//     );

//     // Close the modal after showing error
//     setTimeout(() => {
//       modalClose();
//     }, 1000);
//   };

//   const handleViewOnly = () => {
//     // Navigate to the correct page where room status can be changed
//     navigate('/dashboard/core-modules/property-management-system/master-data/room-management');
//   };

//   // Fetch room details and available room types
//   useEffect(() => {
//     const fetchRoomDetails = async () => {
//       try {
//         const response = await fetch(API_URL);
//         const data = await response.json();

//         if (data && data.rooms && typeof data.rooms === 'object') {
//           const formattedRoomData = {};

//           Object.keys(data.rooms).forEach((category) => {
//             formattedRoomData[category] = {};
//             Object.keys(data.rooms[category]).forEach((status) => {
//               let rooms = data.rooms[category][status];

//               // Check if rooms is an object that contains a 'rooms' array
//               if (rooms && rooms.rooms && Array.isArray(rooms.rooms)) {
//                 formattedRoomData[category][status] = rooms.rooms;
//               } else if (Array.isArray(rooms)) {
//                 // If rooms is already an array, use it directly
//                 formattedRoomData[category][status] = rooms;
//               } else if (typeof rooms === 'object') {
//                 console.warn(
//                   `Expected array for category "${category}" and status "${status}", but got:`,
//                   rooms
//                 );

//                 // Convert the object to an array (e.g., use Object.values to get array of room objects)
//                 formattedRoomData[category][status] = Object.values(rooms);
//               } else {
//                 console.warn(
//                   `Unexpected type for rooms in category "${category}" and status "${status}"`
//                 );
//               }
//             });
//           });

//           setRoomData(formattedRoomData);
//         } else {
//           console.warn('Expected rooms data in an object format, but got:', data);
//         }
//       } catch (error) {
//         console.error('Error fetching room details:', error);
//       }
//     };

//     const fetchAvailableRoomTypes = async () => {
//       try {
//         const response = await fetch(AVAILABILITY_API_URL);
//         const data = await response.json();
//         setAvailableRoomTypes(data.available_room_types);
//       } catch (error) {
//         console.error('Error fetching available room types:', error);
//         setAvailableRoomTypes([]);
//       }
//     };

//     fetchRoomDetails();
//     fetchAvailableRoomTypes();
//   }, []);

//   // Filter rooms based on search criteria
//   const filteredRooms = Object.keys(roomData).map((roomType) => {
//     const rooms = Object.keys(roomData[roomType]).flatMap((status) =>
//       roomData[roomType][status].filter((room) => {
//         const matchesSearch =
//           room.baseprice?.toString().includes(searchTerm) ||
//           room.roomnumber?.toString().includes(searchTerm);
//         const matchesStatus = roomStatus === 'all' || room.status?.toLowerCase() === roomStatus;
//         const matchesRoomType =
//           selectedRoomType === 'all' ||
//           room.room_type?.toLowerCase() === selectedRoomType.toLowerCase();

//         const matchesAvailability =
//           !onlyAvailableRooms || room.status?.toLowerCase() === 'available';

//         return matchesSearch && matchesStatus && matchesRoomType && matchesAvailability;
//       })
//     );

//     // Count the rooms based on their status
//     const availableCount = rooms.filter(
//       (room) => room.status?.toLowerCase() === 'available'
//     ).length;
//     const occupiedCount = rooms.filter((room) => room.status?.toLowerCase() === 'occupied').length;
//     const reservedCount = rooms.filter((room) => room.status?.toLowerCase() === 'reserved').length;
//     const maintenanceCount = rooms.filter(
//       (room) => room.status?.toLowerCase() === 'under maintenance'
//     ).length;

//     // Return the filtered roomType and count of rooms for each status
//     return {
//       roomType,
//       availableCount,
//       occupiedCount,
//       reservedCount,
//       maintenanceCount,
//       rooms,
//     };
//   });

//   const handleClearFilters = () => {
//     setSearchTerm('');
//     setRoomStatus('all');
//     setSelectedRoomType('all');
//     setOnlyAvailableRooms(false);
//   };

//   const openModal = (room) => {
//     setSelectedRoom(room);
//     setIsPopOpen(true);
//   };

//   const modalClose = () => {
//     setIsPopOpen(false);
//     setSelectedRoom(null);
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setSelectedRoom((prevState) => ({
//       ...prevState,
//       [name]: value,
//     }));
//   };

//   return (
//     <div className="p-6 bg-gray-100 min-h-screen">
//       <NavLink
//         to="/dashboard/core-modules/property-management-system/front-desk-operations"
//         className="flex items-center text-gray-400 hover:text-black mb-4"
//       >
//         <FaArrowLeft className="mr-3 text-md" />
//         <span className="text-lg">Back to Front Desk Operations</span>
//       </NavLink>

//       <h1 className="text-2xl font-bold text-center text-black">Room Management</h1>
//       <h1 className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
//         Efficient room management ensures seamless reservations, maintenance and comfort, providing
//         guests with an exceptional experience throughout their stay.
//       </h1>
//       <hr className="border border-gray-400 w-1/2 mx-auto mb-7" />

//       {/* Filters Section */}
//       <div className="flex justify-between items-center gap-2 mb-8 text-sm w-full">
//         <div className="w-full mb-4 sm:mb-0 relative">
//           <input
//             type="text"
//             className="w-full px-2 py-2 pl-10 border border-gray-300 rounded-md"
//             placeholder="Search by Room Number"
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//           />
//           <FiSearch
//             size={16}
//             className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600"
//           />
//         </div>

//         <div className="w-full mb-4 sm:mb-0">
//           <select
//             className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//             value={roomStatus}
//             onChange={(e) => setRoomStatus(e.target.value)}
//           >
//             <option value="all" disabled>
//               Rooms Status
//             </option>
//             <option value="available">Available</option>
//             <option value="reserved">Reserved</option>
//             <option value="occupied">Occupied</option>
//             <option value="under maintenance">Under Maintenance</option>
//           </select>
//         </div>
//         <div className="w-full mb-4 sm:mb-0">
//           <select
//             className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none"
//             value={selectedRoomType}
//             onChange={(e) => {
//               setSelectedRoomType(e.target.value);
//             }}
//           >
//             <option value="all" disabled>
//               All Room Types
//             </option>
//             {roomData &&
//               Object.keys(roomData).map((roomType) => (
//                 <option key={roomType} value={roomType}>
//                   {roomType}
//                 </option>
//               ))}
//           </select>
//         </div>

//         <div className="w-full mr-4">
//           <button
//             onClick={handleClearFilters}
//             className="w-full px-10 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
//           >
//             Clear All
//           </button>
//         </div>
//       </div>

//       {/* Status Cards */}

//       {/* Room Table */}
//       <div className="space-y-8 text-sm">
//         {filteredRooms.map(({ roomType, rooms }) => {
//           if (rooms.length === 0) return null;

//           return (
//             <div key={roomType}>
//               {/* Room Type Header */}
//               <h2 className="bg-white text-black font-bold text-left p-3 text-base rounded-t-lg shadow-md">
//                 {roomType}
//               </h2>

//               {/* Table Container */}
//               <div className="overflow-x-auto rounded-b-lg">
//                 <table className="min-w-full table-auto bg-white">
//                   {/* Table Header */}
//                   <thead className="bg-gray-300 text-black">
//                     <tr>
//                       <th className="py-3 px-6 text-left font-medium w-1/6">Room No.</th>
//                       <th className="py-3 px-6 text-left font-medium w-1/6">Room Type</th>
//                       <th className="py-3 px-6 text-left font-medium w-1/6">Status</th>
//                       <th className="py-3 px-6 text-left font-medium w-1/6">Price</th>
//                     </tr>
//                   </thead>
//                   {/* Table Body */}
//                   <tbody className="text-gray-700 border border-gray-200">
//                     {rooms.map((room, index) => (
//                       <tr
//                         key={room.id || index}
//                         className={`${
//                           index % 2 === 0 ? 'bg-white' : 'bg-gray-100'
//                         } hover:bg-gray-200 cursor-pointer`}
//                         onClick={() => openModal(room)}
//                       >
//                         <td className="py-3 px-6">{room.roomnumber}</td>
//                         <td className="py-3 px-6">{room.room_type}</td>
//                         <td className="py-3 px-6">
//                           <span
//                             className={`px-2 py-1 rounded-full text-xs font-semibold ${
//                               room.status?.toLowerCase() === 'available'
//                                 ? 'bg-green-100 text-green-800'
//                                 : room.status?.toLowerCase() === 'occupied'
//                                   ? 'bg-red-100 text-red-800'
//                                   : room.status?.toLowerCase() === 'reserved'
//                                     ? 'bg-yellow-100 text-yellow-800'
//                                     : 'bg-gray-100 text-gray-800'
//                             }`}
//                           >
//                             {room.status}
//                           </span>
//                         </td>
//                         <td className="py-3 px-6">₹{room.baseprice}</td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             </div>
//           );
//         })}
//       </div>

//       {/* Modal for Viewing Room Details */}
//       {isPopOpen && selectedRoom && (
//         <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
//           <div className="bg-white p-8 rounded-lg shadow-lg w-1/3 text-sm text-black">
//             <h3 className="text-xl font-bold text-center">Room Details</h3>
//             <p className="text-sm text-red-500 mb-4 text-center font-semibold bg-red-50 p-2 rounded-lg">
//               ⚠️ Room status cannot be changed here. Please use Master Data → Room Management.
//             </p>
//             <div className="space-y-4 text-xs">
//               <div>
//                 <label className="block text-black font-medium">Room Number</label>
//                 <input
//                   type="text"
//                   name="roomnumber"
//                   value={selectedRoom.roomnumber}
//                   className="w-full px-4 py-2 border rounded-md bg-gray-100 cursor-not-allowed"
//                   disabled={true}
//                 />
//               </div>
//               <div>
//                 <label className="block text-black font-medium">Room Type</label>
//                 <input
//                   type="text"
//                   name="room_type"
//                   value={selectedRoom.room_type}
//                   className="w-full px-4 py-2 border rounded-md bg-gray-100 cursor-not-allowed"
//                   disabled={true}
//                 />
//               </div>
//               <div>
//                 <label className="block text-black font-medium">Status</label>
//                 <select
//                   name="status"
//                   value={selectedRoom.status}
//                   onChange={handleChange}
//                   className="w-full px-4 py-2 border rounded-md bg-yellow-50 border-yellow-300"
//                 >
//                   <option value="Available">Available</option>
//                   <option value="Reserved">Reserved</option>
//                   <option value="Occupied">Occupied</option>
//                   <option value="Under Maintenance">Under Maintenance</option>
//                 </select>
//                 <p className="text-xs text-red-500 mt-1">
//                   * Status changes are disabled in this view
//                 </p>
//               </div>
//               <div>
//                 <label className="block text-black font-medium">Price</label>
//                 <input
//                   type="number"
//                   name="baseprice"
//                   value={selectedRoom.baseprice}
//                   className="w-full px-4 py-2 border rounded-md bg-gray-100 cursor-not-allowed"
//                   disabled={true}
//                 />
//               </div>
//             </div>

//             <div className="mt-6 flex justify-end gap-3">
//               <button
//                 onClick={modalClose}
//                 className="bg-red-500 text-white hover:bg-red-600 font-semibold px-6 py-2 rounded-md transition-colors"
//               >
//                 Close
//               </button>
//               {/* <button
//                 onClick={handleViewOnly}
//                 className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md transition-colors"
//               >
//                 Go to Room Management
//               </button> */}
//             </div>
//           </div>
//         </div>
//       )}

//       <ToastContainer
//         position="top-center"
//         autoClose={5000}
//         hideProgressBar={false}
//         newestOnTop
//         closeOnClick={false}
//         rtl={false}
//         pauseOnFocusLoss
//         draggable={false}
//         pauseOnHover
//         className="mt-12"
//       />
//     </div>
//   );
// };

// export default RoomAssignment;
